import React, { useState, useEffect } from 'react';
import Modal from '@/components/common/Modal';
import { FaChevronDown, FaChevronRight, FaWrench, FaCalendarAlt, FaUser, FaClock, FaCog, FaDownload, FaFileAlt, FaTachometerAlt, FaTools, FaTag, FaUserTie, FaImages, FaSpinner } from 'react-icons/fa';
import Badge from '@/components/common/Badge';
import moment from 'moment';
import * as mantenimientosApiService from '@/services/mantenimientosApiService';
import { toast } from 'react-hot-toast';
import PatenteDisplay from '@/components/common/PatenteDisplay';
import { ImageComparison } from '@/components/images';
import ImageGalleryWithViewer from '@/components/images/ImageGalleryWithViewer';
import CitaImagenesApiService from '@/services/citaImagenesApiService';
import MantenimientoImagenesApiService from '@/services/mantenimientoImagenesApiService';
import { useSession } from '@clerk/clerk-react';

const HistorialMantenimientos = ({ isOpen, onClose, patente }) => {
  const [mantenimientos, setMantenimientos] = useState([]);
  const [citasFuturas, setCitasFuturas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  
  // Estado para imágenes de mantenimientos
  const [imagenesData, setImagenesData] = useState({});
  const [loadingImagenes, setLoadingImagenes] = useState({});
  
  const { session } = useSession();

  useEffect(() => {
    if (isOpen && patente) {
      cargarHistorial();
    }
  }, [isOpen, patente]);

  const cargarHistorial = async () => {
    setLoading(true);
    try {
      const historial = await mantenimientosApiService.getHistorialVehiculo(patente);
      setMantenimientos(historial.mantenimientos || []);
      setCitasFuturas(historial.citasFuturas || []);
    } catch (error) {
      console.error('Error al cargar historial:', error);
      toast.error('Error al cargar el historial del vehículo');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const getEstadoBadge = (estado, isMantenimiento = false) => {
    // Para mantenimientos finalizados, siempre mostrar como "Completado"
    if (isMantenimiento) {
      return { variant: 'success', label: 'Completado' };
    }
    
    if (!estado) return { variant: 'secondary', label: 'Sin estado' };
    
    const estadoNombre = typeof estado === 'object' ? estado.nombre : estado;
    const estadoMap = {
      'Completado': { variant: 'success', label: 'Completado' },
      'Completada': { variant: 'success', label: 'Completada' },
      'Finalizado': { variant: 'success', label: 'Finalizado' },
      'Finalizada': { variant: 'success', label: 'Finalizada' },
      'En proceso': { variant: 'warning', label: 'En Proceso' },
      'Programada': { variant: 'info', label: 'Programada' },
      'Pendiente': { variant: 'info', label: 'Pendiente' },
      'Cancelada': { variant: 'danger', label: 'Cancelada' }
    };
    return estadoMap[estadoNombre] || { variant: 'secondary', label: estadoNombre };
  };

  const handleDownloadPDF = (mantenimiento) => {
    try {
      //console.log('=== Intentando descargar PDF ===');
      //console.log('Mantenimiento:', mantenimiento);
      //console.log('PDF Base64 disponible:', !!mantenimiento.pdf_base64);
      //console.log('Longitud PDF Base64:', mantenimiento.pdf_base64?.length);
      
      if (!mantenimiento.pdf_base64) {
        console.error('No hay pdf_base64 en el mantenimiento');
        toast.error('No hay documento de garantía disponible para este mantenimiento');
        return;
      }

      // Convertir base64 a blob
      //console.log('Convirtiendo base64 a blob...');
      
      // Limpiar el string base64 removiendo el prefijo data URL si existe
      let base64String = mantenimiento.pdf_base64;
      if (base64String.startsWith('data:')) {
        // Remover el prefijo data:application/pdf;filename=...;base64,
        const base64Index = base64String.indexOf('base64,');
        if (base64Index !== -1) {
          base64String = base64String.substring(base64Index + 7);
        }
      }
      
      //console.log('Base64 limpio, longitud:', base64String.length);
      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // Crear URL y descargar
      //console.log('Creando URL y descargando archivo...');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `garantia_mantenimiento_${mantenimiento.correlativo}_${moment(mantenimiento.fecha).format('YYYY-MM-DD')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      //console.log('PDF descargado exitosamente');
      toast.success('Documento de garantía descargado correctamente');
    } catch (error) {
      console.error('Error al descargar PDF:', error);
      toast.error('Error al descargar el documento de garantía');
    }
  };

  // Cargar imágenes de un mantenimiento específico
  const cargarImagenesMantenimiento = async (mantenimiento) => {
    if (!mantenimiento?.id || loadingImagenes[mantenimiento.id]) return;
    
    setLoadingImagenes(prev => ({ ...prev, [mantenimiento.id]: true }));
    try {
      // Primero obtener el detalle completo del mantenimiento para tener el cita_id
      const detalle = await mantenimientosApiService.getMantenimientoById(mantenimiento.id);
      const citaId = detalle?.cita?.id;
      
      let imagenesAntes = [];
      let imagenesDespues = [];
      
      if (citaId) {
        // Cargar imágenes previas (de la cita)
        imagenesAntes = await CitaImagenesApiService.obtenerImagenesCita(session, citaId);
      }
      
      // Cargar imágenes posteriores (del mantenimiento)
      imagenesDespues = await MantenimientoImagenesApiService.obtenerImagenesMantenimiento(session, mantenimiento.id);
      
      setImagenesData(prev => ({
        ...prev,
        [mantenimiento.id]: {
          imagenesAntes,
          imagenesDespues
        }
      }));
      
    } catch (error) {
      console.error('Error cargando imágenes:', error);
      // No mostrar error toast para no interferir con la UX
    } finally {
      setLoadingImagenes(prev => ({ ...prev, [mantenimiento.id]: false }));
    }
  };

  // Estado para tabs del mantenimiento expandido
  const [activeMantenimientoTab, setActiveMantenimientoTab] = useState({});

  const renderMantenimiento = (mantenimiento) => (
    <div key={mantenimiento.id} className="border border-gray-600 rounded-lg mb-3">
      {/* Header del acordeón */}
      <div 
        className="p-3 md:p-4 cursor-pointer hover:bg-gray-700 transition-colors"
        onClick={() => toggleExpanded(`mant-${mantenimiento.id}`)}
      >
        {/* Versión móvil */}
        <div className="block md:hidden">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center space-x-2 flex-1">
              <span className="text-gray-400 flex-shrink-0">
                {expandedItems[`mant-${mantenimiento.id}`] ? <FaChevronDown /> : <FaChevronRight />}
              </span>
              <FaWrench className="text-blue-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-white text-sm leading-tight">
                  {mantenimiento.cita?.motivo || `Mantenimiento #${mantenimiento.correlativo}`}
                </h4>
                <p className="text-xs text-gray-400 mt-1">
                  Finalizado: {moment(mantenimiento.fecha).format('DD/MM/YYYY')}
                  {mantenimiento.hora_finalizacion && ` - ${mantenimiento.hora_finalizacion.substring(0, 5)}`}
                </p>
                {mantenimiento.cita?.fecha && mantenimiento.cita?.hora && (
                  <p className="text-xs text-orange-400 mt-1">
                    Cita original: {moment(mantenimiento.cita.fecha).format('DD/MM/YYYY')} - {mantenimiento.cita.hora.substring(0, 5)}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <Badge {...getEstadoBadge(mantenimiento.estado, true)} size="sm">
              {getEstadoBadge(mantenimiento.estado, true).label}
            </Badge>
            <div className="flex items-center space-x-2">
              <span className="text-green-400 font-semibold text-sm">
                ${mantenimiento.total?.toLocaleString() || '0'}
              </span>
            </div>
          </div>
          
          {/* Mecánico en vista móvil */}
          <div className="flex items-center mt-2 text-xs">
            <FaUserTie className="text-purple-400 mr-1 flex-shrink-0" />
            <span className="text-gray-400 truncate">
              {mantenimiento.mecanicos?.[0]?.mecanico?.nombre || 'No asignado'}
            </span>
          </div>
        </div>

        {/* Versión desktop */}
        <div className="hidden md:grid grid-cols-12 gap-4 items-center">
          {/* Icono expandir + wrench + título */}
          <div className="col-span-5 flex items-center space-x-3">
            <span className="text-gray-400 flex-shrink-0">
              {expandedItems[`mant-${mantenimiento.id}`] ? <FaChevronDown /> : <FaChevronRight />}
            </span>
            <FaWrench className="text-blue-500 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h4 className="font-semibold text-white truncate">
                {mantenimiento.cita?.motivo || `Mantenimiento #${mantenimiento.correlativo}`}
              </h4>
              <p className="text-sm text-gray-400">
                Finalizado: {moment(mantenimiento.fecha).format('DD/MM/YYYY')}
                {mantenimiento.hora_finalizacion && ` - ${mantenimiento.hora_finalizacion.substring(0, 5)}`}
              </p>
              {mantenimiento.cita?.fecha && mantenimiento.cita?.hora && (
                <p className="text-sm text-orange-400">
                  Cita original: {moment(mantenimiento.cita.fecha).format('DD/MM/YYYY')} - {mantenimiento.cita.hora.substring(0, 5)}
                </p>
              )}
            </div>
          </div>
          
          {/* Estado */}
          <div className="col-span-2 flex justify-center">
            <Badge {...getEstadoBadge(mantenimiento.estado, true)}>
              {getEstadoBadge(mantenimiento.estado, true).label}
            </Badge>
          </div>
          
          {/* Mecánico */}
          <div className="col-span-3 flex items-center">
            <FaUserTie className="text-purple-400 mr-2 flex-shrink-0" title="Mecánico" />
            <span className="text-gray-300 text-sm truncate">
              {mantenimiento.mecanicos?.[0]?.mecanico?.nombre || 'No asignado'}
            </span>
          </div>
          
          {/* Total */}
          <div className="col-span-2 flex items-center justify-end">
            <span className="text-green-500 font-semibold">
              ${mantenimiento.total?.toLocaleString() || '0'}
            </span>
          </div>
        </div>
      </div>

      {/* Contenido expandido */}
      {expandedItems[`mant-${mantenimiento.id}`] && (
        <div className="border-t border-gray-600">
          {/* Información básica siempre visible */}
          <div className="px-3 md:px-4 pt-3 pb-2">
            <div className="border border-gray-600 rounded-lg p-4">
              {/* Fila principal con todos los datos */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
                {/* Mecánico */}
                <div className="lg:col-span-4 flex items-center">
                  <FaUserTie className="text-purple-400 mr-2 flex-shrink-0" title="Mecánico" />
                  <span className="text-gray-300 text-sm truncate">
                    {mantenimiento.mecanicos?.[0]?.mecanico?.nombre || 'No asignado'}
                  </span>
                </div>
                
                {/* Kilometraje */}
                <div className="lg:col-span-3 flex items-center">
                  <FaTachometerAlt className="text-green-400 mr-2 flex-shrink-0" title="Kilometraje" />
                  <span className="text-gray-300 text-sm">
                    {mantenimiento.kilometraje_actual?.toLocaleString() + ' Km' || 'N/A'}
                  </span>
                </div>
                
                {/* Botón de descarga mejorado */}
                <div className="lg:col-span-5 flex justify-center lg:justify-end">
                  {mantenimiento.pdf_base64 ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadPDF(mantenimiento);
                      }}
                      className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors shadow-sm"
                      title="Descargar Documento de Garantía"
                    >
                      <FaFileAlt className="text-sm" />
                      <span className="text-sm font-medium">Documento de Garantía</span>
                      <FaDownload className="text-xs" />
                    </button>
                  ) : (
                    <span className="text-gray-500 text-sm italic bg-gray-600 px-3 py-2 rounded">Sin documento disponible</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Notas Adicionales - siempre visibles */}
          {(mantenimiento.notas) && (
            <div className="px-3 md:px-4 pb-2 space-y-2">
              {mantenimiento.notas && (
                <div className="border border-gray-600 rounded-lg p-3">
                  <h5 className="text-sm font-medium text-white mb-2 flex items-center">
                    <FaFileAlt className="mr-2 text-orange-400" />
                    Observaciones
                  </h5>
                  {/* Línea divisoria */}
                  <div className="border-t border-gray-600 mb-3"></div>
                  <p className="text-white text-sm">{mantenimiento.notas}</p>
                </div>
              )}
            </div>
          )}



          {/* Tabs */}
          <div className="px-3 md:px-4 py-3">
            <div className="flex space-x-1 bg-gray-800 p-1 rounded-lg border border-gray-700">
              {[
                { id: 'servicios', label: 'Servicios', icon: FaTools },
                { id: 'repuestos', label: 'Repuestos', icon: FaCog },
                { id: 'imagenes', label: 'Imágenes', icon: FaImages },
                { id: 'costos', label: 'Costos', icon: FaTag }
              ].map(tab => {
                const IconComponent = tab.icon;
                const isActive = (activeMantenimientoTab[mantenimiento.id] || 'servicios') === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveMantenimientoTab({...activeMantenimientoTab, [mantenimiento.id]: tab.id});
                      // Cargar imágenes al abrir el tab de imágenes
                      if (tab.id === 'imagenes' && !imagenesData[mantenimiento.id]) {
                        cargarImagenesMantenimiento(mantenimiento);
                      }
                    }}
                    className={`
                      flex-1 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 py-2 sm:py-3 px-1 sm:px-4 rounded-lg 
                      text-xs sm:text-sm font-medium transition-all duration-200 border
                      ${isActive
                        ? 'bg-blue-600 text-white border-blue-500 shadow-lg'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700 border-transparent hover:border-gray-600'
                      }
                    `}
                  >
                    <IconComponent className="text-sm flex-shrink-0" />
                    <span className="text-xs sm:text-sm truncate">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="px-3 md:px-4 py-3">

            {/* Tab Servicios */}
            {(activeMantenimientoTab[mantenimiento.id] || 'servicios') === 'servicios' && (
              <div>
                {mantenimiento.servicios && mantenimiento.servicios.length > 0 ? (
                  <div className="space-y-3">
                    {mantenimiento.servicios.map((servicioItem, index) => (
                      <div key={index} className="border border-gray-600 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-medium text-white">
                              {servicioItem.servicio?.nombre || 'Servicio sin nombre'}
                            </h4>
                            {servicioItem.servicio?.descripcion && (
                              <p className="text-gray-400 text-sm mt-1">
                                {servicioItem.servicio.descripcion}
                              </p>
                            )}
                            {servicioItem.observaciones && (
                              <p className="text-gray-300 text-sm mt-2">
                                <strong>Observaciones:</strong> {servicioItem.observaciones}
                              </p>
                            )}
                            {servicioItem.mecanico_observaciones && (
                              <p className="text-gray-300 text-sm mt-2">
                                <strong>Observaciones del mecánico:</strong> {servicioItem.mecanico_observaciones}
                              </p>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-lg font-medium text-white">
                              ${servicioItem.precio?.toLocaleString() || '0'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <div className="border-t border-gray-600 pt-3 mt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-semibold">Total Servicios:</span>
                        <span className="text-green-400 font-bold text-lg">
                          ${mantenimiento.total_servicios?.toLocaleString() || '0'}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-400">No hay servicios registrados</p>
                )}
              </div>
            )}

            {/* Tab Repuestos */}
            {activeMantenimientoTab[mantenimiento.id] === 'repuestos' && (
              <div>
                {mantenimiento.repuestos && mantenimiento.repuestos.length > 0 ? (
                  <div className="space-y-3">
                    {mantenimiento.repuestos.map((repuestoItem, index) => (
                      <div key={index} className="border border-gray-600 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-medium text-white">
                              {repuestoItem.repuesto?.nombre || 'Repuesto sin nombre'}
                            </h4>
                            {repuestoItem.repuesto?.marca && (
                              <p className="text-gray-400 text-sm">
                                Marca: {repuestoItem.repuesto.marca}
                              </p>
                            )}
                            <div className="text-gray-300 text-sm mt-1">
                              Cantidad: {repuestoItem.cantidad}
                            </div>
                            {repuestoItem.observaciones && (
                              <p className="text-gray-300 text-sm mt-2">
                                <strong>Observaciones:</strong> {repuestoItem.observaciones}
                              </p>
                            )}
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-sm text-gray-400">
                              ${repuestoItem.precio_unitario?.toLocaleString() || '0'} c/u
                            </div>
                            <div className="text-lg font-medium text-white">
                              ${repuestoItem.precio_total?.toLocaleString() || '0'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <div className="border-t border-gray-600 pt-3 mt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-semibold">Total Repuestos:</span>
                        <span className="text-green-400 font-bold text-lg">
                          ${mantenimiento.total_repuestos?.toLocaleString() || '0'}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-400">No hay repuestos registrados</p>
                )}
              </div>
            )}

            {/* Tab Imágenes */}
            {activeMantenimientoTab[mantenimiento.id] === 'imagenes' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  {/* Título - oculto en móvil */}
                  <h3 className="hidden md:flex text-lg font-medium text-white items-center">
                    <FaImages className="mr-2 text-purple-400" />
                    Imágenes del Mantenimiento
                  </h3>
                  
                  {/* En móvil, solo mostrar los stats centrados */}
                  <div className="flex items-center gap-3 w-full md:w-auto justify-center md:justify-end">
                    {loadingImagenes[mantenimiento.id] && (
                      <div className="flex items-center text-gray-400">
                        <FaSpinner className="animate-spin mr-2" size={14} />
                        <span className="text-sm">Cargando...</span>
                      </div>
                    )}
                    
                    {/* Stats cards - solo si hay imágenes y no están cargando */}
                    {!loadingImagenes[mantenimiento.id] && imagenesData[mantenimiento.id] && 
                     (imagenesData[mantenimiento.id].imagenesAntes.length > 0 || 
                      imagenesData[mantenimiento.id].imagenesDespues.length > 0) && (
                      <div className="flex gap-2">
                        {imagenesData[mantenimiento.id].imagenesAntes.length > 0 && (
                          <div className="bg-blue-600 bg-opacity-20 border border-blue-500/30 rounded-lg py-2 w-[70px] flex flex-col items-center justify-center">
                            <div className="text-blue-400 text-lg font-bold">{imagenesData[mantenimiento.id].imagenesAntes.length}</div>
                            <div className="text-blue-300 text-xs">Previo</div>
                          </div>
                        )}
                        {imagenesData[mantenimiento.id].imagenesDespues.length > 0 && (
                          <div className="bg-green-600 bg-opacity-20 border border-green-500/30 rounded-lg py-2 w-[70px] flex flex-col items-center justify-center">
                            <div className="text-green-400 text-lg font-bold">{imagenesData[mantenimiento.id].imagenesDespues.length}</div>
                            <div className="text-green-300 text-xs">Posterior</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {loadingImagenes[mantenimiento.id] ? (
                  <div className="text-center py-8">
                    <FaSpinner className="animate-spin mx-auto text-blue-400 mb-3" size={24} />
                    <p className="text-gray-400">Cargando imágenes...</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Mostrar comparador solo si hay imágenes antes Y después */}
                    {imagenesData[mantenimiento.id]?.imagenesAntes.length > 0 && 
                     imagenesData[mantenimiento.id]?.imagenesDespues.length > 0 ? (
                      <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-600/30">
                        <h4 className="text-white font-medium mb-4 flex items-center">
                          <FaImages className="mr-2 text-blue-400" />
                          Comparación Antes/Después
                        </h4>
                        <ImageComparison 
                          imagenesAntes={imagenesData[mantenimiento.id].imagenesAntes}
                          imagenesDespues={imagenesData[mantenimiento.id].imagenesDespues}
                        />
                      </div>
                    ) : (
                      <>
                        {/* Mostrar solo galería de imágenes previas si solo hay antes */}
                        {imagenesData[mantenimiento.id]?.imagenesAntes.length > 0 && (
                          <ImageGalleryWithViewer
                            images={imagenesData[mantenimiento.id].imagenesAntes}
                            title="Fotos Estado Previo"
                            showDeleteButton={false}
                            viewerTitle={`Fotos Estado Previo - ${patente || 'Vehículo'}`}
                            className="bg-gray-800/50 border-gray-600/30"
                          />
                        )}

                        {/* Mostrar solo galería de imágenes posteriores si solo hay después */}
                        {imagenesData[mantenimiento.id]?.imagenesDespues.length > 0 && (
                          <ImageGalleryWithViewer
                            images={imagenesData[mantenimiento.id].imagenesDespues}
                            title="Fotos Estado Posterior"
                            showDeleteButton={false}
                            viewerTitle={`Fotos Estado Posterior - ${patente || 'Vehículo'}`}
                            className="bg-gray-800/50 border-gray-600/30"
                          />
                        )}
                      </>
                    )}

                    {/* Mensaje si no hay imágenes */}
                    {(!imagenesData[mantenimiento.id] || 
                      (imagenesData[mantenimiento.id].imagenesAntes.length === 0 && 
                       imagenesData[mantenimiento.id].imagenesDespues.length === 0)) && (
                      <div className="text-center py-8">
                        <FaImages className="mx-auto text-gray-500 mb-3" size={32} />
                        <p className="text-gray-400">No hay imágenes disponibles para este mantenimiento</p>
                        <p className="text-gray-500 text-sm mt-2">
                          Las imágenes se capturan durante el proceso de finalización del mantenimiento
                        </p>
                      </div>
                    )}

                  </div>
                )}
              </div>
            )}

            {/* Tab Costos */}
            {activeMantenimientoTab[mantenimiento.id] === 'costos' && (
              <div>

                <div className="border border-gray-600 rounded-lg p-6">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Subtotal Servicios:</span>
                      <span className="text-white">${mantenimiento.total_servicios?.toLocaleString() || '0'}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-300">Subtotal Repuestos:</span>
                      <span className="text-white">${mantenimiento.total_repuestos?.toLocaleString() || '0'}</span>
                    </div>
                    
                    {/* Items Adicionales - Mostrar detalle */}
                    {mantenimiento.items_adicionales && mantenimiento.items_adicionales.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-gray-300 font-medium pt-2">Items Adicionales:</div>
                        {mantenimiento.items_adicionales.map((item, index) => (
                          <div key={index} className="flex justify-between pl-4">
                            <span className="text-gray-400 text-sm">
                              {item.descripcion || 'Item adicional'} ({item.cantidad || 1})
                            </span>
                            <span className={`text-sm ${item.precio_total < 0 ? 'text-red-400' : 'text-white'}`}>
                              ${item.precio_total?.toLocaleString() || '0'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="border-t border-gray-600 pt-3">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Subtotal:</span>
                        <span className="text-white">${mantenimiento.subtotal?.toLocaleString() || '0'}</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-300">IVA ({mantenimiento.porcentaje_iva || 19}%):</span>
                      <span className="text-white">${mantenimiento.monto_iva?.toLocaleString() || '0'}</span>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-600 pt-4 mt-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span className="text-white">Total:</span>
                    <span className="text-green-400">${mantenimiento.total?.toLocaleString() || '0'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderCitaFutura = (cita) => (
    <div key={cita.id} className="border border-gray-600 rounded-lg mb-3">
      {/* Header del acordeón */}
      <div 
        className="p-3 md:p-4 cursor-pointer hover:bg-gray-700 transition-colors"
        onClick={() => toggleExpanded(`cita-${cita.id}`)}
      >
        {/* Versión móvil */}
        <div className="block md:hidden">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center space-x-2 flex-1">
              <span className="text-gray-400 flex-shrink-0">
                {expandedItems[`cita-${cita.id}`] ? <FaChevronDown /> : <FaChevronRight />}
              </span>
              <FaCalendarAlt className="text-orange-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-white text-sm leading-tight">
                  {cita.motivo || 'Cita Programada'}
                </h4>
                <p className="text-xs text-gray-400 mt-1">
                  {moment(cita.fecha).format('DD/MM/YYYY')} - {cita.hora?.substring(0, 5)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Badge {...getEstadoBadge(cita.estado)} size="sm">
                {getEstadoBadge(cita.estado).label}
              </Badge>
              {cita.tiempo_total && (
                <Badge variant="info" size="sm">
                  <FaClock className="mr-1" size={10} />
                  {cita.tiempo_total} min
                </Badge>
              )}
            </div>
            {cita.servicios_cita && cita.servicios_cita.length > 0 && (
              <span className="text-blue-400 font-semibold text-sm">
                ${cita.servicios_cita.reduce((total, servicio) => total + (servicio.precio_estimado || 0), 0).toLocaleString()}
              </span>
            )}
          </div>
        </div>

        {/* Versión desktop */}
        <div className="hidden md:grid grid-cols-12 gap-4 items-center">
          {/* Icono expandir + calendario + título */}
          <div className="col-span-6 flex items-center space-x-3">
            <span className="text-gray-400 flex-shrink-0">
              {expandedItems[`cita-${cita.id}`] ? <FaChevronDown /> : <FaChevronRight />}
            </span>
            <FaCalendarAlt className="text-orange-500 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h4 className="font-semibold text-white truncate">
                {cita.motivo || 'Cita Programada'}
              </h4>
              <p className="text-sm text-gray-400">
                {moment(cita.fecha).format('DD/MM/YYYY')} - {cita.hora?.substring(0, 5)}
              </p>
            </div>
          </div>
          
          {/* Estado */}
          <div className="col-span-2 flex justify-center">
            <Badge {...getEstadoBadge(cita.estado)}>
              {getEstadoBadge(cita.estado).label}
            </Badge>
          </div>
          
          {/* Duración */}
          <div className="col-span-2 flex justify-center">
            {cita.tiempo_total && (
              <Badge variant="info" size="sm">
                <FaClock className="mr-1" size={10} />
                {cita.tiempo_total} min
              </Badge>
            )}
          </div>
          
          {/* Precio */}
          <div className="col-span-2 text-right">
            {cita.servicios_cita && cita.servicios_cita.length > 0 && (
              <span className="text-blue-500 font-semibold">
                ${cita.servicios_cita.reduce((total, servicio) => total + (servicio.precio_estimado || 0), 0).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Contenido expandido */}
      {expandedItems[`cita-${cita.id}`] && (
        <div className="px-3 md:px-4 pb-4 border-t border-gray-600">
          <div className="mt-3 space-y-4">
            {/* Información básica compacta */}
            <div className="border border-gray-600 rounded-lg p-3">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <FaClock className="text-gray-400 mr-2" />
                  <span className="text-gray-300">Duración: {cita.tiempo_total || 60} min</span>
                </div>
                <div className="flex items-center">
                  <FaCalendarAlt className="text-gray-400 mr-2" />
                  <span className="text-gray-300">
                    {cita.hora?.substring(0, 5)} - {cita.hora_fin?.substring(0, 5) || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Motivo y Detalles en grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {cita.motivo && (
                <div className="border border-gray-600 rounded-lg p-3">
                  <h5 className="text-gray-400 text-xs uppercase mb-3">Motivo:</h5>
                  {/* Línea divisoria */}
                  <div className="border-t border-gray-600 mb-3"></div>
                  <p className="text-white">{cita.motivo}</p>
                </div>
              )}

              {cita.detalles && (
                <div className="border border-gray-600 rounded-lg p-3">
                  <h5 className="text-gray-400 text-xs uppercase mb-3">Detalles:</h5>
                  {/* Línea divisoria */}
                  <div className="border-t border-gray-600 mb-3"></div>
                  <p className="text-white">{cita.detalles}</p>
                </div>
              )}
            </div>

            {/* Servicios programados */}
            {cita.servicios_cita && cita.servicios_cita.length > 0 && (
              <div>
                <h5 className="text-blue-400 font-medium mb-3 flex items-center">
                  <FaTools className="mr-2" />
                  Servicios Programados
                </h5>
                
                <div className="space-y-3">
                  {cita.servicios_cita.map((servicioItem, index) => (
                    <div key={index} className="border border-gray-600 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium text-white">
                            {servicioItem.servicio?.nombre || 'Servicio sin nombre'}
                          </h4>
                          {servicioItem.servicio?.descripcion && (
                            <p className="text-gray-400 text-sm mt-1">
                              {servicioItem.servicio.descripcion}
                            </p>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-lg font-medium text-white">
                            ${servicioItem.precio_estimado?.toLocaleString() || '0'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <div className="border-t border-gray-600 pt-3 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-white font-semibold">Total Estimado:</span>
                      <span className="text-green-400 font-bold text-lg">
                        ${cita.servicios_cita.reduce((total, servicio) => total + (servicio.precio_estimado || 0), 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const modalTitle = (
    <div className="flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <span>Historial de Mantenimientos</span>
        {(mantenimientos.length > 0 || citasFuturas.length > 0) && (
          <p className="text-gray-400 text-sm font-normal mt-1 truncate">
            {mantenimientos[0]?.cita?.cliente?.nombre 
              ? `Cliente: ${mantenimientos[0].cita.cliente.nombre} ${mantenimientos[0].cita.cliente.apellido}` 
              : citasFuturas[0]?.cliente?.nombre 
                ? `Cliente: ${citasFuturas[0].cliente.nombre} ${citasFuturas[0].cliente.apellido}`
                : ''}
          </p>
        )}
      </div>
      <div className="flex items-center flex-shrink-0">
        <PatenteDisplay patente={patente} size="sm" />
      </div>
    </div>
  );

  const modalFooter = (
    <div className="flex justify-end">
      <button
        onClick={onClose}
        className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm sm:text-base"
      >
        Cerrar
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="2xl"
      footer={modalFooter}
    >
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Citas futuras */}
          {citasFuturas.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                <FaCalendarAlt className="text-orange-500 mr-2" />
                Citas Programadas
              </h3>
              <div className="space-y-3">
                {citasFuturas.map(renderCitaFutura)}
              </div>
            </div>
          )}

          {/* Historial de mantenimientos */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
              <FaCog className="text-blue-500 mr-2" />
              Historial de Mantenimientos
            </h3>
            
            {mantenimientos.length > 0 ? (
              <div className="space-y-3">
                {mantenimientos.map(renderMantenimiento)}
              </div>
            ) : (
              <div className="text-center py-8">
                <FaWrench className="text-gray-500 text-4xl mx-auto mb-4" />
                <p className="text-gray-400">
                  No hay mantenimientos registrados para este vehículo
                </p>
              </div>
            )}
          </div>

          {/* Mensaje cuando no hay datos */}
          {mantenimientos.length === 0 && citasFuturas.length === 0 && !loading && (
            <div className="text-center py-8">
              <FaWrench className="text-gray-500 text-4xl mx-auto mb-4" />
              <p className="text-gray-400">
                No hay historial disponible para este vehículo
              </p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default HistorialMantenimientos;